import Logo2 from "../../assets/Logo2.png";
import Button from "../../components/Buttons/Button";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { secondary } from "../../theme";
import { useApi } from "../../hooks/api";
import { useGlobal } from "../../hooks/global";
import { ModalLoading } from "../../components/Loadings/ModalLoading";
import { GenericForm } from "../../components/GenericForm";
import {
  PageContainer,
  PageCard,
  PageHeader,
  PageForm,
  PageFooter,
  PageTitle,
  PageDescription,
  PageLogo,
  PageTerms,
  ExternalLink,
  LinkHighlight,
} from "../shared/authLayout";
import { TERMS_URL, getStoreUrl } from "../shared/constants";
import { createFormGroups } from "./formGroups";

const FORM_ID = "new-account-form";

export const NewAccount = () => {
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const { notify } = useGlobal();
  const { api } = useApi();

  const {
    register,
    handleSubmit,
    control,
    watch,
    getValues,
    formState: { errors },
    setError,
    setValue,
    trigger,
  } = useForm({ mode: "onChange" });

  const formValues = watch();
  const groups = useMemo(() => createFormGroups(getValues), [getValues]);

  useEffect(() => {
    const code = params.get("code");
    const trackingCode = params.get("tracking_code");

    if (!code || !trackingCode) return;

    api
      .post("/acquisition-campaign-shots/increment-clicks", {
        recommendation_code: code,
        tracking_code: trackingCode,
      })
      .catch((error) => console.error("Error tracking campaign click:", error));
  }, [api, params]);

  const onSubmit = handleSubmit(async (data) => {
    setLoading(true);

    try {
      await api.post("/user", {
        accept_policy_term: true,
        email: data.email,
        email_confirmation: data.confirmEmail,
        password: data.password.trim(),
        password_confirmation: data.confirmPassword.trim(),
        entry_type: "mobile",
        origin: params.get("origin") ?? "",
        code: params.get("code") ?? "",
        campaign: params.get("campaign") ?? null,
        ativador: params.get("ativador") ?? null,
      });

      window.location.href = getStoreUrl();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "SERVER ERROR";

      if (message === "User already exists!") {
        setError("email", { type: "manual", message: "Email já cadastrado" });
        return;
      }

      notify(
        "Erro ao tentar realizar cadastro, tente novamente mais tarde",
        "error"
      );
    } finally {
      setLoading(false);
    }
  });

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.stopPropagation();
    void onSubmit(event);
  };

  return (
    <PageContainer>
      <PageCard>
        <PageHeader>
          <PageLogo src={Logo2} alt="Inside Brasil" />
          <PageTitle>💸 Aqui sua opinião tem valor!</PageTitle>
          <PageDescription>
            Faça seu cadastro e comece a ganhar dinheiro com a Inside Brasil
          </PageDescription>
        </PageHeader>

        <PageForm id={FORM_ID} onSubmit={handleFormSubmit}>
          <GenericForm
            groups={groups}
            _form={formValues}
            control={control}
            trigger={trigger}
            errors={errors}
            setValue={setValue}
            register={register}
            noTrigger
          />
        </PageForm>

        <PageFooter>
          <Button
            type="submit"
            form={FORM_ID}
            color="#FFF"
            background={secondary.light}
            style={{ fontSize: 15, minHeight: 48, padding: "12px 16px" }}
            disabled={loading}
          >
            Criar conta
          </Button>

          <PageTerms>
            Ao criar sua conta, você concorda com os{" "}
            <ExternalLink
              target="_blank"
              rel="noopener noreferrer"
              href={TERMS_URL}
            >
              <LinkHighlight>termos de uso</LinkHighlight>
            </ExternalLink>
            .
          </PageTerms>
        </PageFooter>
      </PageCard>

      <ModalLoading loading={loading} theme="light" />
    </PageContainer>
  );
};
